import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, Download, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import DroneIcon from "../components/DroneIcon";

// Public shared-video preview page. No auth required.
export default function SharedVideo() {
  const { slug } = useParams();
  const [video, setVideo] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/videos/share/${slug}`);
        setVideo(data);
        setState("ready");
      } catch (e) {
        setError(e?.response?.data?.detail || "Video bulunamadı");
        setState("error");
      }
    })();
  }, [slug]);

  const videoUrl = video?.video_url
    ? (video.video_url.startsWith("http") ? video.video_url : `${process.env.REACT_APP_BACKEND_URL}${video.video_url}`)
    : null;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-[#C29B62]/10 blur-[140px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#7a5e34]/10 blur-[120px] rounded-full" />
      </div>

      <nav className="relative h-16 border-b border-white/5 bg-slate-950/60 backdrop-blur-2xl">
        <div className="max-w-6xl mx-auto h-full px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#C29B62] to-[#7a5e34] flex items-center justify-center">
              <DroneIcon size={16} className="text-slate-950" />
            </div>
            <span className="font-display font-black tracking-tight">ProSanalDrone</span>
          </Link>
          <Link to="/" data-testid="shared-cta"
            className="text-sm text-[#C29B62] hover:text-[#D4AF7A] inline-flex items-center gap-1">
            Sen de üret <ArrowRight size={14} />
          </Link>
        </div>
      </nav>

      <div className="relative max-w-5xl mx-auto px-6 py-12 md:py-20">
        {state === "loading" && (
          <div className="flex items-center gap-3 text-slate-400 text-sm tracking-[0.2em] uppercase">
            <Loader2 size={16} className="animate-spin" /> Video yükleniyor…
          </div>
        )}

        {state === "error" && (
          <div className="flex items-start gap-3 p-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300">
            <AlertCircle size={20} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-lg text-white mb-1">Video bulunamadı</div>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {state === "ready" && video && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            <div className="mb-8">
              <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-3">
                {video.company_name}
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
                {video.il} / {video.ilce}
                <span className="text-slate-500 mx-3 text-2xl">·</span>
                <span className="text-[#C29B62]">{video.ada_parsel || (video.ada && video.parsel ? `${video.ada}/${video.parsel}` : video.ada || video.parsel)}</span>
              </h1>
              <p className="text-slate-400 mt-3">
                {video.mahalle} Mahallesi
                {video.zoning_info && <> · {video.zoning_info}</>}
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden border border-[#C29B62]/30 bg-slate-900/40">
              <div className="relative">
                <video src={videoUrl} poster={video.thumbnail_url} controls autoPlay muted
                  data-testid="shared-video-player"
                  className="w-full aspect-[9/16] bg-black max-w-[420px] mx-auto block" />
                {video.logo_data && (
                  <img src={video.logo_data} alt="watermark"
                    className="absolute top-4 right-4 max-w-[100px] max-h-[60px] opacity-80 pointer-events-none" />
                )}
              </div>
              <div className="p-6 md:p-8 border-t border-white/10">
                <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-3">Sunum</div>
                <p className="text-slate-100 text-lg leading-relaxed">{video.narration}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              {videoUrl && (
                <a href={videoUrl} download data-testid="shared-download-btn"
                  className="px-5 py-3 rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] transition inline-flex items-center gap-2">
                  <Download size={16} /> Videoyu İndir
                </a>
              )}
              <Link to="/" data-testid="shared-signup-cta"
                className="px-5 py-3 rounded-md border border-white/15 hover:bg-white/5 transition inline-flex items-center gap-2">
                Kendi arsanız için üretin <ArrowRight size={14} />
              </Link>
            </div>

            <div className="mt-16 pt-8 border-t border-white/5 text-center text-slate-500 text-sm">
              Bu video <span className="text-[#C29B62]">ProSanalDrone</span> ile üretildi ·
              {" "}<Link to="/" className="underline hover:text-white">prosanaldrone.com</Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
